'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import classes from "./wedding_locations_section.module.css";
import "./wedding_locations_section.css";
import EmptyProviderLogo from '@/assets/png/empty-event-logo.png';
import SafeImage from '@/components/common/safe_image';
import customImageLoader from '../../../../imageLoader';
import CustomSVG from '@/components/common/svg';
import MapPin from '@/assets/svg/icons/map-pin.svg';
import CustomButton from '@/components/common/buttons/button';
import ArrowRight from '@/assets/svg/icons/arrow-right.svg';
import ChevronLeft from '@/assets/svg/icons/chevron-left.svg';
import ChevronRight from '@/assets/svg/icons/chevron-right.svg';

const max_shown_img = 3;
const LOCATION_CARD_SIZES = '(max-width: 940px) 300px, 450px';
const LOCATION_SUBIMAGE_SIZES = '(max-width: 940px) 100px, 149px';

async function triggerSlideHaptic() {
    try {
        const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
        await Haptics.impact({ style: ImpactStyle.Light });
    } catch (error) {
        // no-op on web / unsupported platforms
    }
}

export default function WeddingLocationsSection({providers, no_title, no_margin, no_background, unique_class_affix}) {
    const navigationClassSuffix = unique_class_affix ? `-${unique_class_affix}` : "";
    const paginationClassName = `swiper-custom-pagination${navigationClassSuffix}`;
    const prevButtonClassName = `locations-swiper-button-prev${navigationClassSuffix}`;
    const nextButtonClassName = `locations-swiper-button-next${navigationClassSuffix}`;
    const hasMultipleProviders = (providers?.providers?.length ?? 0) > 1;

    return <div className={`locations-section-main ${classes["locations-section-main"]} ${no_background ? classes["no_background"] : ""} ${no_margin ? classes["no_margin"] : ""}`}>
        {!no_title && <div className="d-flex flex-column align-items-center gap-4 text-center padding-x-8">
            <h2 className={`margin-0 color-gray-800 display-sm-bold ${classes["locations-section-main-title"]}`}>Esküvői helyszínek</h2>
            <p className={`margin-0 color-gray-800 display-xs-regular ${classes["locations-section-sub-title"]}`}>Fedezd fel azokat a helyszíneket, amelyek stílusban, méretben és hangulatban is illenek a nagy napotok elképzeléseihez.</p>
        </div>}
        {providers && providers.providers && providers.providers.length > 0 && <div className="d-flex flex-column w-100">
            <Swiper
                slidesPerView={"auto"}
                spaceBetween={30}
                freeMode={{
                    enabled: true,
                    sticky: true,
                }}
                pagination={{
                    el: `.${paginationClassName}`,
                    type: 'fraction',
                    renderFraction: (currentClass) =>
                        `<span class="${currentClass}"></span>/<span>${providers?.providers?.length ?? 0}</span>`,
                }}
                navigation={hasMultipleProviders ? {
                    nextEl: `.${nextButtonClassName}`,
                    prevEl: `.${prevButtonClassName}`,
                    disabledClass: 'locations-swiper-button-disabled'
                } : false}
                onSlideChange={triggerSlideHaptic}
                modules={[FreeMode, Navigation, Pagination]}
                className={`${classes["locations-swiper"]}`}
                slideToClickedSlide={false}
                watchSlidesProgress={true}
                threshold={10}
                noSwipingClass="swiper-no-swiping"
                grabCursor={true}
            >
                {providers.providers.map(p => <SwiperSlide key={`location-provider-item-${p.id}`} className={`${classes["location-swiper-item"]}`}>
                    <div className={`${classes["location-swiper-item-main-img"]}`}>
                        {p.images && p.images.length > 0
                            ? <img
                                src={customImageLoader({ src: p.images[0], width: 640, quality: 75 })}
                                srcSet={`${customImageLoader({ src: p.images[0], width: 640, quality: 75 })} 640w, ${customImageLoader({ src: p.images[0], width: 1080, quality: 75 })} 1080w`}
                                sizes={LOCATION_CARD_SIZES}
                                alt={p.name}
                                loading="lazy"
                                decoding="async"
                                style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover', inset: 0 }}
                              />
                            : <SafeImage sizes={LOCATION_CARD_SIZES} src={EmptyProviderLogo} alt={"Szolgáltató képe"} />
                        }
                    </div>
                    {p.images && p.images.length > 1 && <div className={`${classes["location-swiper-item-sub-imgs-container"]}`}>
                        {p.images.slice(1, max_shown_img+1).map((img, i) => <div key={`location-provider-item-${p.id}-${i}`} className={`${classes["location-swiper-item-sub-img"]}`}>
                            <img
                                src={customImageLoader({ src: img, width: 640, quality: 75 })}
                                srcSet={`${customImageLoader({ src: img, width: 640, quality: 75 })} 640w, ${customImageLoader({ src: img, width: 1080, quality: 75 })} 1080w`}
                                sizes={LOCATION_SUBIMAGE_SIZES}
                                alt={`${p.name} kép ${i}`}
                                loading="lazy"
                                decoding="async"
                                style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover', inset: 0 }}
                            />
                        </div>)}
                    </div>}
                    <div className={`${classes["location-swiper-item-text-container"]}`}>
                        <div className="d-flex flex-column align-self-stretch text-center">
                            <h3 className={`${classes["provider-card-title"]} margin-0 color-gray-800 text-lg-bold w-100`}>{p.name}</h3>
                            <div className="d-flex flex-row gap-1 align-items-center justify-content-center w-100 text-center">
                                <CustomSVG className={`circle-stroke-gray-700 stroke-gray-700 flex-shrink-0`} SvgFunction={MapPin} height={18} width={18} />
                                <p className="margin-0 color-gray-700 text-sm-regular text-truncate">{(p.address && p.address.city) ? p.address.city : "-"}</p>
                            </div>
                        </div>
                        <div className='w-100 swiper-no-swiping'><CustomButton className="w-100" size="lg" hierarchy="primary" href={`/${p.url}`} rel="noopener noreferrer" target="_blank" >Kapcsolatfelvétel</CustomButton></div>
                    </div>
                </SwiperSlide>)}
            </Swiper>
            {hasMultipleProviders && <div className={classes["locations-swiper-controls"]}>
                <button type="button" className={`${prevButtonClassName} ${classes["locations-swiper-nav-button"]}`} aria-label="Előző helyszín">
                    <CustomSVG className="stroke-gray-700" SvgFunction={ChevronLeft} height={20} width={20} />
                </button>
                <div className={`${paginationClassName} ${classes["swiper-custom-pagination"]}`} />
                <button type="button" className={`${nextButtonClassName} ${classes["locations-swiper-nav-button"]}`} aria-label="Következő helyszín">
                    <CustomSVG className="stroke-gray-700" SvgFunction={ChevronRight} height={20} width={20} />
                </button>
            </div>}
        </div>}
        {!no_title && <div><CustomButton size="lg" hierarchy="primary" icon="trailing" is_svg={true} svg={ArrowRight} href={"/helyszinek"} >További helyszínek</CustomButton></div>}
    </div>
}